import { BreadcrumbGroup, FormField, Header, SpaceBetween, Table } from '@cloudscape-design/components';
import React, { useEffect, useMemo, useState } from 'react';
import Favorites from '../../../../../components/extras/Favorites';
import { addNotification, addNotificationStatus, buildBreadcrumbItems, getCookie, getUrlVersion, setData, setLoading, setParameters } from '../../../../commons/functions';
import { useGlobalState } from '../../../../context/GlobalStateContext';
import { CustomAppLayout } from '../../../layout';
const _preserve = React.version;

import { mdiAlert, mdiCircleSmall, mdiHumanMaleBoard, mdiTimerOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { endOfMonth, format, parse, startOfMonth, subMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import MPModal from '../../../../../components/MPModal';
import { fetchWithWatch } from '../../../../../hooks/fetchWithWatch';
type TableItem = {
  displayName: React.ReactNode;
  Total: number | React.ReactNode;
  [key: string]: number | React.ReactNode; // allow month keys and React nodes
};

interface ReportItem {
  idUser: number;
  displayName: string;
  [key: string]: any; // This allows F_2026_01, C_2026_01, etc.
}

const generateMonthRange = (endDate: Date) => {
  return Array.from({ length: 12 }, (_, i) => {
    const d = subMonths(startOfMonth(endDate), 11 - i);

    return {
      id: format(d, "yyyy_MM"),
      label: format(d, "MM yy", { locale: es })
    };
  });
};
export const ReportsEDVInstructionPage = () => {
  const { menu, parameters } = useGlobalState();

  const [report, setReport] = useState<ReportItem[]>([]);
  const reportEndDate = (() => {
    if (parameters.period && parameters.period !== "0") {
      // 1. Parse '2026-04' into a Date object (defaults to the 1st of that month)
      const monthStart = parse(parameters.period, "yyyy-MM", new Date());

      // 2. Automatically get the correct last day (30th, 31st, or 28th/29th)
      return endOfMonth(monthStart);
    }

    return new Date();
  })();
  const monthRange = useMemo(() => generateMonthRange(reportEndDate), [reportEndDate]);

  const handleRefreshReport = async () => {
    //if (parameters.startDate && parameters.endDate) {
    if (parameters.year) {
      try {
        setLoading(true)
        const response = await fetchWithWatch(`${getUrlVersion()}/routing/v1/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-routing': '/private/reports/edv/instruction',
            'JWT': getCookie("JWT"),
            //  
          },
          body: JSON.stringify({
            until: format(reportEndDate, "dd-MM-yyyy")
          }),
          //mode: 'no-cors'  // Bypasses CORS but limits the response
        });

        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`);
        }
        const data = await response.json();
        addNotificationStatus(data, setData);
        if (data.code == 0) {
          const transformed = data.report.map((item: any) => {
            const totalTime = monthRange.reduce((sum, m) => sum + Number(item[`F_${m.id}`] || 0), 0);
            const totalChecks = monthRange.reduce((sum, m) => sum + Number(item[`C_${m.id}`] || 0), 0);

            // 1. Create the base dynamic data first
            const dynamicData: any = {};
            monthRange.forEach(m => {
              dynamicData[`V${m.id}`] = Number(item[`F_${m.id}`] || 0);
              dynamicData[`C${m.id}`] = Number(item[`C_${m.id}`] || 0);
            });

            // 2. Combine them so totals are physically at the end of the object
            return {
              displayName: item.displayName,
              ...dynamicData, // Spreads all V_ID and T_ID keys here
              totalTime: Number(totalTime.toFixed(2)),
              totalChecks: totalChecks
            };
          });

          setReport(transformed);
        }
      } catch (error: any) {
        addNotification(`Error de red y/o comunicaciones: ${error.message}`, "error", setData);
      } finally { setLoading(false) }

    };
  }

  const [sortingColumn, setSortingColumn] = useState<any>({ sortingField: "displayName" });
  const [isDescending, setIsDescending] = useState(false);

  const columns = useMemo(() => [
    {
      id: "displayName",
      header: "Instructor",
      cell: (item: any) =>
        <FormField
          label={item.displayName}
          info={item.totalTime === 0 && item.totalChecks === 0 ? <Icon path={mdiAlert} color="red" size={0.75} /> : undefined}
        />,
      sortingField: "displayName",
    },
    ...monthRange.map((m) => ({
      id: m.id,
      header: m.label.charAt(0).toUpperCase() + m.label.slice(1),
      minWidth: 80,
      width: 90,
      // The sorting happens on the raw flight time
      //sortingField: `V${m.id}`,
      cell: (item: any) => {
        const fVal = item[`V${m.id}`];
        const cVal = item[`C${m.id}`];

        if (fVal === 0) {
          return <Icon path={mdiCircleSmall} size={1} color='red' />;
        }

        return (
          <FormField
            label={`${fVal?.toFixed(2) || "0"}`}
            stretch
          >
            {cVal || 0}
          </FormField>
        );
      },
    })),
    {
      id: "time",
      header: <Icon path={mdiTimerOutline} size={1} />,
      cell: (item: any) => item.totalTime.toFixed(2),
      minWidth: 70,
      width: 80,
      sortingField: "time",
      sortingComparator: (a: any, b: any) => a.totalTime - b.totalTime,
    },
    {
      id: "checks",
      header: <Icon path={mdiHumanMaleBoard} size={1} />,
      cell: (item: any) => item.totalChecks,
      minWidth: 50,
      width: 70,
      sortingField: "checks",
      sortingComparator: (a: any, b: any) => a.totalChecks - b.totalChecks
    },
  ], [monthRange]);


  const sortedReport = useMemo(() => {
    const data = [...report];
    const { sortingField } = sortingColumn;

    const columnDefinition = columns.find(col => (col as any).sortingField === sortingField) as any;
    const comparator = columnDefinition?.sortingComparator;

    data.sort((a, b) => {
      let result = 0;
      if (comparator) {
        result = comparator(a, b);
      } else if (sortingField) {
        const valA = a[sortingField as keyof ReportItem];
        const valB = b[sortingField as keyof ReportItem];

        // Check if both values are numbers
        if (typeof valA === 'number' && typeof valB === 'number') {
          result = valA - valB;
        } else {
          // Fallback for strings
          const strA = (valA ?? "").toString().toLowerCase();
          const strB = (valB ?? "").toString().toLowerCase();
          result = strA.localeCompare(strB);
        }
      }
      return isDescending ? -result : result;
    });

    return data;
  }, [report, sortingColumn, isDescending, columns]);

  useEffect(() => {
    handleRefreshReport();
  }, [parameters.period]);
  return (
    <CustomAppLayout
      toolsHide
      contentType="form"
      breadcrumbs={
        <BreadcrumbGroup
          items={buildBreadcrumbItems(menu)}
          expandAriaLabel="Show path"
          ariaLabel="Breadcrumbs"
        />
      }
      content={
        <>
          <SpaceBetween direction="vertical" size="xs">

            <Header actions={
              <SpaceBetween direction="horizontal" size="xs">

                <Favorites />
              </SpaceBetween>
            }
              description="Reporte de instrucción de pilotos agrupados por horas de instrucción y chequeos realizados"
            >
              Reporte de instrucción



            </Header>
            <Table
              columnDefinitions={columns}
              items={sortedReport} // Pass the SORTED list here
              sortingColumn={sortingColumn}
              sortingDescending={isDescending}
              onSortingChange={(e) => {
                setSortingColumn(e.detail.sortingColumn);
                setIsDescending(e.detail.isDescending);
              }}
              stickyHeader
              stickyColumns={{ first: 1, last: 2 }}
              filter={
                <SpaceBetween direction="horizontal" size="xs">
                  <FormField label="Periodo de corte">
                    <div style={{ width: 130 }}>
                      <MPModal
                        width={120}
                        today prevMonth nextMonth
                        end={new Date()}
                        date={`${parameters.period}`}
                        onConfirm={(date) => {
                          setParameters({ ...parameters, period: date.period, month: date.month, year: date.year });
                        }}
                      />
                    </div>
                  </FormField>
                </SpaceBetween>
              }
              variant="embedded"
            />
          </SpaceBetween>
        </>
      }
    />
  );
};
